/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import Terminal from '../shared/entities/terminal';
import { TerminalPlatformsEnum } from '../shared/entities/terminalPlatform';
import isNil from 'lodash/isNil';

export const shouldRequestFlightPath = (terminal: Terminal) => {
  return terminal && terminal.platformType && terminal.platformType !== TerminalPlatformsEnum.GroundNomadic;
};

/**
 * Returns true if the current platform type is included in the list of platform
 * types to show.
 * @param currentPlatformType The platform type of the terminal.
 * @param platformTypesToShow The platform types that will return true.
 */
export const isPlatformTypeInList = (
  currentPlatformType: TerminalPlatformsEnum,
  ...platformTypesToShow: TerminalPlatformsEnum[]
) => {
  if (platformTypesToShow) {
    return platformTypesToShow.filter((type: TerminalPlatformsEnum) => currentPlatformType === type).length > 0;
  }

  return false;
};

/**
 * Return true if the value is not null or undefined, and the terminal platform type if supported; false otherwise.
 * @param value the value to check if null or platform type is unsupported for this value.
 * @param currentPlatformType the platform type of the terminal.
 * @param unsupportedPlatformTypes the unsupported platform types for this value.
 */
export const isNotNullOrUndefinedOrUnsupported = (
  value: any,
  currentPlatformType: TerminalPlatformsEnum,
  ...unsupportedPlatformTypes: TerminalPlatformsEnum[]
) => {
  return !isNil(value) && !isPlatformTypeInList(currentPlatformType, ...unsupportedPlatformTypes);
};

/**
 * Return the value if supported and not null or undefined; or 'N/A' placeholder for unsupported platforms;
 * or the string placeholder if supported but the value is null or undefined.
 * @param value the value to pass through and check for null/undefined or the platform type
 * @param currentPlatformType the platform for the terminal that the value is associated with
 * @param valueFormatter a function that takes in input the value and returns the formatted value
 * @param unsupportedPlatformTypes the unsupported platform types for this value
 */
export const valuePlaceholderOrNAForUnsupportedPlatformTypes = (
  value: number,
  nullUndefinedPlaceholder: string,
  unsupportedPlaceholder: string,
  currentPlatformType: TerminalPlatformsEnum,
  valueFormatter?: (value: number) => string,
  ...unsupportedPlatformTypes: TerminalPlatformsEnum[]
): string => {
  if (isPlatformTypeInList(currentPlatformType, ...unsupportedPlatformTypes)) {
    return unsupportedPlaceholder;
  }

  if (isNil(value)) {
    return nullUndefinedPlaceholder;
  }

  if (!isNil(valueFormatter)) {
    return valueFormatter(value);
  }

  return value.toString();
};
